/**
 * useDubGenerateProgress.ts — PLAT-10761
 *
 * Smooth estimated progress for the "Generating dub…" footer. The dub engine
 * exposes no percentage — `generateDub` is a single blocking request that
 * resolves with coverage — so, like the publish drawer's `usePublishProgress`,
 * this interpolates over elapsed time with an exponential-decay curve toward a
 * cap below 100, ratcheted monotonic. There are no discrete status pushes here,
 * so it's purely time-based against an expected duration (the render-segment
 * portion of the publish idiom, standalone). It only reaches 100 when the caller
 * marks the run finished.
 */

import { useEstimatedProgress } from '@src/components/common/useEstimatedProgress';
import { estimateDubGenerateMs } from '@src/config/processingEstimates';

/** Hard cap while synthesizing — the real completion is signalled separately. */
export const DUB_PROGRESS_CAP = 95;
export const DUB_TICK_MS = 500;
// Same decay as the publish/processing curves: ~86% of the span reached at the
// expected ETA, still creeping toward the cap when a run outlasts the estimate.
const DUB_DECAY_RATE = 2;

export interface DubProgressInputs {
  videoDurationSec?: number;
  speakerCount?: number;
}

export interface DubProgressRun {
  /** Expected wall-clock of the run, computed once when it started. */
  etaMs: number;
  /** Epoch ms the run started — the estimate continues across a remount. */
  startedAt: number;
}

/**
 * Monotonic 0–100 progress while a dub is generating. Ticks every 500ms while
 * `active` and caps at {@link DUB_PROGRESS_CAP} — the real completion is
 * signalled separately, so the bar never needs to render a real 100.
 *
 * Pass the store's `DubRun` (PLAT-11570) and the clock is anchored to when the
 * request actually started, so the popover and the on-page chip agree, and
 * reopening the popover mid-run continues instead of restarting from 0%.
 * Passing plain inputs instead anchors the clock to when `active` flipped on.
 */
export function useDubGenerateProgress(
  active: boolean,
  inputs: DubProgressInputs | DubProgressRun,
): number {
  const isRun = 'etaMs' in inputs;
  const etaMs = isRun ? inputs.etaMs : estimateDubGenerateMs(inputs);
  // The shared time-interpolation hook, tuned with the dub constants above
  // (extracted in PLAT-11563 so the music separation row could reuse it).
  return useEstimatedProgress(active, etaMs, {
    cap: DUB_PROGRESS_CAP,
    tickMs: DUB_TICK_MS,
    decay: DUB_DECAY_RATE,
    startedAt: isRun ? inputs.startedAt : undefined,
  });
}
